import { Outlet, useLocation } from "@tanstack/react-router"
import { LogIn, TriangleAlert } from "lucide-react"
import { useEffect } from "react"
import { RedirectOnce } from "@/components/redirect"
import { ConsoleShell } from "@/components/shell/ConsoleShell"
import { Button } from "@/components/ui/button"
import { isValidScopeId, rememberScope, type Scope } from "@/lib/connection"
import { useLocale, useT } from "@/lib/i18n"
import { DefinitionsLiveProvider } from "@/lib/live"
import { useScope } from "@/lib/scope"
import { useCell, useSession } from "@/lib/session"
import { usePrincipal } from "@/widget/runtime"
import { LensProvider } from "@/workspace/lens"

export function ScopeLayout() {
  const { connection, auth } = useSession()
  const location = useLocation()
  // The in-app path to come back to after signing in (path + query; never an absolute URL).
  const here = `${location.pathname}${location.searchStr}`
  const scope = useScope()
  const { orgId, environmentId } = scope
  const valid = isValidScopeId(orgId) && isValidScopeId(environmentId)
  const t = useT()
  useEffect(() => {
    if (connection && valid) rememberScope({ orgId, environmentId })
  }, [connection, valid, orgId, environmentId])
  if (!connection) return <RedirectOnce to="/connect" search={{ returnTo: here }} />
  if (!valid) return <RedirectOnce to="/" />
  return (
    <DefinitionsLiveProvider scope={scope}>
      <LensProvider scope={scope}>
      <LocaleFollowsPrincipal scope={scope} />
      <ConsoleShell scope={scope}>
        <main className="flex min-h-0 min-w-0 flex-1 flex-col">
          {auth.kind === "oidc" && auth.status === "expired" ? (
            <div
              role="alert"
              className="flex items-center gap-3 border-b border-warning/30 bg-warning-soft px-4 py-2 text-sm text-warning"
            >
              <TriangleAlert className="size-4 shrink-0" aria-hidden />
              <span className="min-w-0 flex-1">{t("shell.sessionEnded")}</span>
              <Button size="sm" onClick={() => void auth.signIn(here)}>
                <LogIn className="rtl-mirror" /> {t("shell.signInAgain")}
              </Button>
            </div>
          ) : null}
          <Outlet />
        </main>
      </ConsoleShell>
      </LensProvider>
    </DefinitionsLiveProvider>
  )
}

/** The locale preference belongs to the user the cell resolved, not to the browser. Rendered only once connected. */
function LocaleFollowsPrincipal({ scope }: { scope: Scope }) {
  const { connection } = useCell()
  const principal = usePrincipal(scope)
  const { setUser } = useLocale()
  const actor = principal.status === "ready" ? principal.actor : null
  useEffect(() => {
    if (actor) setUser(`${connection.baseUrl}#${actor}`)
  }, [actor, connection.baseUrl, setUser])
  return null
}
